import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useQueryString from '../hooks/queryParams/useQueryString';
import { useOrganizationStore } from '../store/organizationStore';
import { fetchOrganization, setOrganization } from '../api/organization';

interface WithSystemIdProps {
  systemId: string;
}

const withSystemId =
  <P extends WithSystemIdProps>(WrappedComponent: React.ComponentType<P>) =>
  (props: Omit<P, 'systemId'>) => {
    const history = useHistory();
    const systemId = localStorage.getItem('systemGid');
    const { queryParams: getQueryParam } = useQueryString();

    const organizationId = getQueryParam.get('organizationId');

    // get organization store info from zustand
    const { organizationInfo } = useOrganizationStore();

    const appendQueryParam = useCallback(
      (paramName: string, paramValue: string) => {
        if (
          Boolean(getQueryParam.get('systemId')) ||
          Boolean(getQueryParam.get('organizationId'))
        )
          return;
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set(paramName, paramValue);
        history.replace({ search: queryParams.toString() });
      },
      [history, getQueryParam]
    );

    useEffect(() => {
      // landing directly on an organization link (deep link, post-login
      // redirect): the store may not know this organization yet
      if (!organizationId || organizationId === organizationInfo?.id) return;
      // switching away (e.g. org -> system) briefly leaves the old
      // organizationId in the URL after the store is cleared; if the URL or
      // store moved on before the response lands, the fetch is stale
      let stale = false;
      fetchOrganization(organizationId).then((organization) => {
        if (organization && !stale) {
          setOrganization(organization);
        }
      });
      return () => {
        stale = true;
      };
    }, [organizationId, organizationInfo?.id]);

    useEffect(() => {
      const orgId = organizationId || organizationInfo?.id;
      const queryParamsName = orgId ? 'organizationId' : 'systemId';
      const queryParamsValue = orgId || systemId;
      // Only append the query param if we have a valid value
      if (queryParamsValue) {
        appendQueryParam(queryParamsName, queryParamsValue);
      }
    }, [systemId, organizationInfo, appendQueryParam, organizationId]);

    return (
      <WrappedComponent
        {...(props as P)}
        systemId={systemId}
        organizationId={organizationInfo?.id}
      />
    );
  };

export default withSystemId;
